<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';

import { useCheckout } from '@/composables/useCheckout';
import { NutsLineItem } from '@/lib/cart/lineItem';

defineProps<{ lineItems: NutsLineItem[] }>();

const store = useStore();
const { cxMode } = useCheckout(store);
const collapsed = ref(false);
const collapsibleState = computed(() =>
  collapsed.value
    ? {
        text: 'Expand',
        icon: nutshell['img/arrow-down.svg'],
        class: 'md:h-14 md:overflow-hidden',
      }
    : {
        text: 'Collapse',
        icon: nutshell['img/arrow-up.svg'],
        class: '',
      },
);

onMounted(() => {
  if (window.innerWidth > 768 && !cxMode.value) collapsed.value = true;
});

const toggle = () => {
  collapsed.value = !collapsed.value;
};

const panelBorderUtilities = 'md:border md:border-solid md:border-gray-300 md:rounded-3xl';
</script>

<template>
  <div class="order-line-items" data-test="order-line-items-section">
    <div
      class="px-4 pb-4 bg-white md:pl-8 md:pr-6 md:pt-3.5 md:pb-4"
      :class="[panelBorderUtilities, collapsibleState.class]"
    >
      <!-- eslint-disable-next-line max-len -->
      <div
        class="flex items-center justify-between pt-4 pb-4 border-t border-solid border-neutral-300 md:border-none md:pt-0"
      >
        <h3 class="text-base font-semibold text-black md:text-lg font-sophia-pro">
          Items in order ({{ lineItems.length }})
        </h3>
        <span
          class="items-center hidden text-xs text-black underline cursor-pointer md:visible md:flex md:mr-6"
          @click="toggle"
          @keyup.enter="toggle"
          tabindex="0"
        >
          {{ collapsibleState.text }}
          <img :src="collapsibleState.icon" class="w-3 h-3 ml-2 fill-current" alt="toggle icon" />
        </span>
      </div>
      <div class="overflow-y-scroll styled-scrollbar">
        <slot />
      </div>
    </div>
    <slot name="footer" />
  </div>
</template>

<style lang="scss" scoped>
.styled-scrollbar {
  &::-webkit-scrollbar {
    width: 6px;
    height: auto;
    background-color: $alto;
    border-radius: 9999px;
    scrollbar-color: $dark-grey $alto;

    &-thumb {
      background: $dark-grey;
      border-radius: 9999px;
    }
  }
}
</style>
